import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Upload, ArrowLeft, Users, Link as LinkIcon, Check, Download, Share2, X } from "lucide-react";
import { Logo } from "../../components/Logo";
import { API, authHeaders, formatError } from "../../lib/adminApi";
import { waPhone } from "../../lib/templateRender";

const SAMPLE_CSV = `name,company,city,phone,email,team_size,rera,address,pincode,registered_at,notes
Rajesh Kumar,Kumar Properties,Mumbai,919820012345,rajesh@example.com,5,A51900012345,"Shop 12, MG Road, Andheri West",400001,2026-09-21,Met at property expo
Priya Sharma,,Pune,919850054321,priya@example.com,,B52100067890,"Flat 304, Sunshine Residency, Baner",411045,2026-09-22,Individual CP
Apex Realty Group,Apex Realty,Thane,919123456780,info@apexrealty.in,25,C51700099887,"Office 801, Orion Business Park",400601,2026-09-23,Large organisation`;

const downloadSampleCsv = () => {
  const blob = new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "cp-import-sample.csv";
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 500);
};

export default function AdminChannelPartners() {
  const navigate = useNavigate();
  const [cps, setCps] = useState([]);
  const [team, setTeam] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [assignTo, setAssignTo] = useState("");
  const [assignIds, setAssignIds] = useState(new Set());
  const [assignOpen, setAssignOpen] = useState(false);
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);
  const [autoAssign, setAutoAssign] = useState("");
  const [uploadMsg, setUploadMsg] = useState("");
  const [linkFor, setLinkFor] = useState(null); // cp object
  const [linkProg, setLinkProg] = useState("");
  const [linkResult, setLinkResult] = useState(null); // {url, program_title}
  const [copied, setCopied] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({});
  const [addSaving, setAddSaving] = useState(false);

  const ADD_FIELDS = [
    ["name", "Name *"], ["company", "Company"], ["city", "City"], ["phone", "Phone (91…)"],
    ["email", "Email"], ["team_size", "Team size"], ["rera", "RERA / Cert"], ["pincode", "Pincode"],
  ];

  const saveNewCp = async () => {
    if (!(addForm.name || "").trim()) { setErr("Name is required"); return; }
    setAddSaving(true); setErr("");
    try {
      const payload = { ...addForm, name: addForm.name.trim() };
      if (payload.team_size) payload.team_size = parseInt(payload.team_size) || null;
      const { data: cp } = await axios.post(`${API}/admin/cp`, payload, { headers: authHeaders() });
      setAddOpen(false); setAddForm({});
      await load();
      openLinkModal(cp); // straight to link generation for the new CP
    } catch (e) { setErr(formatError(e)); }
    finally { setAddSaving(false); }
  };

  const load = async () => {
    try {
      const [c, t, p] = await Promise.all([
        axios.get(`${API}/admin/cp`, { headers: authHeaders() }),
        axios.get(`${API}/admin/team-users`, { headers: authHeaders() }),
        axios.get(`${API}/admin/onboarding/programs`, { headers: authHeaders() }),
      ]);
      setCps(c.data || []); setTeam(t.data || []); setPrograms(p.data || []);
      try {
        const s = await axios.get(`${API}/admin/settings`, { headers: authHeaders() });
        setAutoAssign(s.data?.partner_auto_assign_to || "");
      } catch {}
    } catch (e) { setErr(formatError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const uploadCsv = async (file) => {
    setUploadMsg("Uploading…");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/cp/upload-csv`, fd, { headers: authHeaders() });
      setUploadMsg(`Inserted ${data.inserted} · Skipped ${data.skipped}${data.errors?.length ? ` · ${data.errors.length} errors` : ""}`);
      load();
    } catch (e) { setUploadMsg(formatError(e)); }
  };

  const toggle = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };

  const toggleAll = () => {
    if (pageRows.length > 0 && pageRows.every((c) => selected.has(c.id))) {
      const n = new Set(selected); pageRows.forEach((c) => n.delete(c.id)); setSelected(n);
    } else {
      const n = new Set(selected); pageRows.forEach((c) => n.add(c.id)); setSelected(n);
    }
  };

  const bulkAssign = async () => {
    if (selected.size === 0) return;
    if (assignIds.size === 0) {
      await axios.post(`${API}/admin/cp/assign`, { cp_ids: Array.from(selected), team_user_id: null }, { headers: authHeaders() });
    } else {
      await axios.post(`${API}/admin/cp/assign`, { cp_ids: Array.from(selected), team_user_ids: Array.from(assignIds) }, { headers: authHeaders() });
    }
    setSelected(new Set());
    setAssignOpen(false);
    load();
  };

  const saveAutoAssign = async (uid) => {
    setAutoAssign(uid);
    try { await axios.put(`${API}/admin/settings`, { partner_auto_assign_to: uid || null }, { headers: authHeaders() }); } catch (e) { setErr(formatError(e)); }
  };

  const totalPages = pageSize === 0 ? 1 : Math.max(1, Math.ceil(cps.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = pageSize === 0 ? cps : cps.slice(safePage * pageSize, (safePage + 1) * pageSize);

  const openLinkModal = (cp) => {
    setLinkFor(cp); setLinkResult(null); setCopied(false);
    const first = programs.find((p) => p.active);
    setLinkProg(first ? first.id : "");
  };

  const createLink = async () => {
    if (!linkProg) return;
    setErr("");
    try {
      // Admin JWT works on the team invite endpoint (role check passes for admins).
      const { data: inv } = await axios.post(`${API}/team/invites`, { cp_id: linkFor.id, program_id: linkProg }, { headers: authHeaders() });
      const url = `${window.location.origin}/onboard/${inv.cp_name_slug}/${inv.token}`;
      setLinkResult({ url, program_title: inv.program_title });
    } catch (e) { setErr(formatError(e)); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(linkResult.url); } catch {}
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  const nameOf = (uid) => team.find((t) => t.id === uid)?.name || (uid ? "Unknown" : "—");

  return (
    <div className="min-h-screen bg-white/60">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3"><Logo /> <span className="font-heading font-bold text-lg">Channel Partners</span></div>
        <button onClick={() => navigate("/admin/dashboard")} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#FF5E1A]"><ArrowLeft className="w-4 h-4" /> Dashboard</button>
      </header>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="glass-strong rounded-2xl p-5 mb-6 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">CSV Upload</div>
            <div className="text-xs text-slate-500 mt-1">Columns: name, company, city, phone, email, team_size, rera, address, pincode, registered_at, notes</div>
            {uploadMsg && <div className="text-xs text-emerald-700 mt-1">{uploadMsg}</div>}
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setAddForm({}); setAddOpen(true); }} data-testid="cp-add-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] hover:bg-slate-800 text-white px-4 py-2 text-sm font-semibold">
              <Users className="w-4 h-4" /> Add CP
            </button>
            <button onClick={downloadSampleCsv} data-testid="cp-sample-csv-btn"
              className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-300 hover:border-[#FF5E1A] px-4 py-2 text-sm font-semibold">
              <Download className="w-4 h-4" /> Sample CSV
            </button>
            <label className="inline-flex items-center gap-2 rounded-full bg-[#FF5E1A] hover:bg-[#E04F00] text-white px-4 py-2 text-sm font-semibold cursor-pointer" data-testid="cp-csv-upload-label">
              <Upload className="w-4 h-4" /> Upload CSV
              <input type="file" accept=".csv" className="hidden" data-testid="cp-csv-upload-input"
                onChange={(e) => e.target.files?.[0] && uploadCsv(e.target.files[0])} />
            </label>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <h2 className="font-heading text-lg font-bold flex-1">{cps.length} Channel Partners</h2>
          <label className="text-xs text-slate-600 inline-flex items-center gap-1.5">
            Auto-assign API registrations to
            <select value={autoAssign} onChange={(e) => saveAutoAssign(e.target.value)} data-testid="cp-autoassign-select"
              className="px-2.5 py-1.5 rounded-xl glass-input text-xs">
              <option value="">Nobody (manual)</option>
              {team.filter((t) => t.active).map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </label>
          <div className="relative">
            <button onClick={() => setAssignOpen(!assignOpen)} data-testid="cp-assign-open"
              className="px-3 py-2 rounded-xl bg-white border border-slate-300 text-sm font-semibold hover:border-[#FF5E1A]">
              Assign to… {assignIds.size > 0 && `(${assignIds.size})`}
            </button>
            {assignOpen && (
              <div className="absolute right-0 top-full mt-1 z-20 bg-white rounded-2xl shadow-xl border border-slate-200 p-3 w-56" data-testid="cp-assign-panel">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Select one or more members — CPs are split evenly</div>
                {team.filter((t) => t.active).map((t) => (
                  <label key={t.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer">
                    <input type="checkbox" checked={assignIds.has(t.id)} data-testid={`cp-assign-member-${t.id}`}
                      onChange={() => { const n = new Set(assignIds); n.has(t.id) ? n.delete(t.id) : n.add(t.id); setAssignIds(n); }} />
                    {t.name} <span className="text-[10px] text-slate-400">{t.email}</span>
                  </label>
                ))}
                <button onClick={() => setAssignIds(new Set())} className="mt-2 text-[11px] text-slate-500 hover:text-red-600">Clear selection (unassign)</button>
              </div>
            )}
          </div>
          <button onClick={bulkAssign} disabled={selected.size === 0}
            data-testid="cp-bulk-assign-btn"
            className="rounded-full bg-[#2A5CB8] hover:bg-[#1F4B9C] text-white px-4 py-2 text-sm font-semibold disabled:opacity-40 inline-flex items-center gap-1">
            <Users className="w-4 h-4" /> Assign ({selected.size})
          </button>
        </div>

        {err && <div className="text-sm text-red-600 mb-3">{err}</div>}
        {loading ? <div className="text-slate-500">Loading…</div> : (
          <div className="glass-strong rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/60 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-3 text-left"><input type="checkbox" data-testid="cp-select-all" onChange={toggleAll} checked={pageRows.length > 0 && pageRows.every((c) => selected.has(c.id))} /></th>
                  <th className="text-left px-3 py-3">Name</th>
                  <th className="text-left px-3 py-3">Company / City</th>
                  <th className="text-left px-3 py-3">Contact</th>
                  <th className="text-left px-3 py-3">Assigned to</th>
                  <th className="text-left px-3 py-3">Link</th>
                </tr>
              </thead>
              <tbody data-testid="cp-list">
                {pageRows.map((cp) => (
                  <tr key={cp.id} className="border-t border-slate-100" data-testid={`cp-row-${cp.id}`}>
                    <td className="px-3 py-2"><input type="checkbox" checked={selected.has(cp.id)} onChange={() => toggle(cp.id)} data-testid={`cp-select-${cp.id}`} /></td>
                    <td className="px-3 py-2 font-semibold">{cp.name}</td>
                    <td className="px-3 py-2 text-slate-600">{[cp.company, cp.city].filter(Boolean).join(" · ")}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{cp.phone} {cp.email && `· ${cp.email}`}</td>
                    <td className="px-3 py-2 text-xs">{nameOf(cp.assigned_to)}</td>
                    <td className="px-3 py-2">
                      <button onClick={() => openLinkModal(cp)} data-testid={`cp-create-link-${cp.id}`} title="Create onboarding link"
                        className="p-1.5 rounded-lg text-slate-500 hover:text-[#FF5E1A] hover:bg-white"><LinkIcon className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
                {pageRows.length === 0 && <tr><td colSpan="6" className="px-4 py-6 text-center text-slate-500">No CPs yet. Upload a CSV to get started.</td></tr>}
              </tbody>
            </table>
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-white/60 flex-wrap gap-2">
              <div className="text-xs text-slate-500" data-testid="cp-page-info">
                {cps.length === 0 ? "0" : `${safePage * (pageSize || cps.length) + 1}–${Math.min((safePage + 1) * (pageSize || cps.length), cps.length)}`} of {cps.length}
              </div>
              <div className="flex items-center gap-2">
                <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                  data-testid="cp-page-size" className="px-2 py-1.5 rounded-lg glass-input text-xs">
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                  <option value={0}>All</option>
                </select>
                <button onClick={() => setPage(Math.max(0, safePage - 1))} disabled={safePage === 0} data-testid="cp-page-prev"
                  className="px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-xs font-semibold disabled:opacity-40">Prev</button>
                <button onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))} disabled={safePage >= totalPages - 1} data-testid="cp-page-next"
                  className="px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-xs font-semibold disabled:opacity-40">Next</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add CP modal */}
      {addOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center px-4 py-6 overflow-y-auto" onClick={() => setAddOpen(false)}>
          <div className="bg-white rounded-3xl w-full max-w-lg p-6 shadow-2xl my-6" onClick={(e) => e.stopPropagation()} data-testid="cp-add-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg">Add Channel Partner</h3>
              <button onClick={() => setAddOpen(false)} className="p-1 text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              {ADD_FIELDS.map(([key, label]) => (
                <input key={key} type={key === "team_size" ? "number" : "text"} placeholder={label}
                  value={addForm[key] || ""} onChange={(e) => setAddForm({ ...addForm, [key]: e.target.value })}
                  data-testid={`cp-add-${key}`} className="px-3 py-2 rounded-xl glass-input text-sm" />
              ))}
            </div>
            <input type="text" placeholder="Address" value={addForm.address || ""}
              onChange={(e) => setAddForm({ ...addForm, address: e.target.value })}
              data-testid="cp-add-address" className="w-full mt-2.5 px-3 py-2 rounded-xl glass-input text-sm" />
            <textarea placeholder="Notes (optional)" value={addForm.notes || ""}
              onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
              data-testid="cp-add-notes" className="w-full mt-2.5 px-3 py-2 rounded-xl glass-input text-sm min-h-[60px]" />
            {err && <div className="text-xs text-red-600 mt-2">{err}</div>}
            <button onClick={saveNewCp} disabled={addSaving} data-testid="cp-add-save"
              className="mt-4 w-full rounded-2xl bg-[#FF5E1A] hover:bg-[#E04F00] text-white px-4 py-3 text-sm font-bold disabled:opacity-40">
              {addSaving ? "Saving…" : "Save & create link"}
            </button>
          </div>
        </div>
      )}

      {/* Create-link modal */}
      {linkFor && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center px-4 py-6 overflow-y-auto" onClick={() => setLinkFor(null)}>
          <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl my-6" onClick={(e) => e.stopPropagation()} data-testid="cp-link-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg">Onboarding link · {linkFor.name}</h3>
              <button onClick={() => setLinkFor(null)} className="p-1 text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
            </div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Choose training / program</label>
            <select value={linkProg} onChange={(e) => setLinkProg(e.target.value)} data-testid="cp-link-program"
              className="w-full mt-1 px-3 py-2 rounded-xl glass-input text-sm">
              {programs.filter((p) => p.active).map((p) => (
                <option key={p.id} value={p.id}>{p.title}</option>
              ))}
            </select>
            {!linkResult ? (
              <button onClick={createLink} disabled={!linkProg} data-testid="cp-link-generate"
                className="mt-4 w-full rounded-2xl bg-[#FF5E1A] hover:bg-[#E04F00] text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-40">
                Generate link
              </button>
            ) : (
              <div className="mt-4 rounded-2xl bg-emerald-50 border border-emerald-200 p-4" data-testid="cp-link-result">
                <div className="text-xs font-bold text-emerald-700 mb-1">Link ready · {linkResult.program_title}</div>
                <div className="text-xs font-mono break-all text-slate-700">{linkResult.url}</div>
                <div className="flex gap-2 mt-3">
                  <button onClick={copyLink} data-testid="cp-link-copy"
                    className="flex-1 rounded-xl bg-white border border-slate-300 px-3 py-2 text-xs font-semibold inline-flex items-center justify-center gap-1">
                    {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <LinkIcon className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy"}
                  </button>
                  <a href={`https://wa.me/${waPhone(linkFor.phone)}?text=${encodeURIComponent(`Namaste ${linkFor.name} ji, aapka personal onboarding link: ${linkResult.url}`)}`}
                    target="_blank" rel="noopener noreferrer" data-testid="cp-link-whatsapp"
                    className="flex-1 rounded-xl bg-[#25D366] text-white px-3 py-2 text-xs font-semibold inline-flex items-center justify-center gap-1">
                    <Share2 className="w-3.5 h-3.5" /> WhatsApp
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
